import { AxiosError } from 'axios';
import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { useGet } from 'hooks/use-get';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { getScenarioStatus } from 'views/bluviu-nutrition/api/get-scenario-status';
import { getScenario } from '../api/bluviu-nutrition';
import { Scenario } from '../types/scenario.type';
import { ScenariosParams } from '../types/scenarios-params.type';

type State = {
  scenarioQuery: UseQueryResult<Scenario, AxiosError<unknown, any>>;
  scenarioStatusQuery: UseQueryResult<{ scenarioIsRunning: boolean }, AxiosError<unknown, any>>;
};

const ScenarioContext = React.createContext<State | undefined>(undefined);

type SiteSettingsProviderProps = {
  children: React.ReactNode;
};

const ScenarioProvider: React.FC<SiteSettingsProviderProps> = ({ children }) => {
  const params = useParams<ScenariosParams>();
  const queryClient = useQueryClient();
  const snackbarDispatch = useSnackbarDispatch();
  const {
    account: { company },
  } = useAccountState();
  const companyId = company?.companyId;

  const [scenarioStatusRefetchInterval, setScenarioStatusRefetchInterval] = useState<number | false | undefined>(false);
  const previousScenarioRunning = useRef<{ key: string; isRunning: boolean }>();

  const scenarioQuery = useGet(
    ['getScenario', company!.companyId, params.scenarioId],
    ({ queryKey }) => getScenario(String(queryKey[1]), String(queryKey[2])),
    {
      enabled: !!params.scenarioId,
    }
  );

  const scenarioStatusQuery = useGet(
    ['getScenarioStatus', params.globalSiteId, params.scenarioId],
    ({ queryKey }) => getScenarioStatus(String(queryKey[1]), String(queryKey[2])),
    {
      enabled: !!scenarioStatusRefetchInterval,
      refetchInterval: scenarioStatusRefetchInterval,
    }
  );
  useEffect(() => {
    const status = scenarioStatusQuery.data;
    if (!status || !companyId) return;

    const statusKey = `${params.globalSiteId}:${params.scenarioId}`;
    const previousStatus = previousScenarioRunning.current;

    setScenarioStatusRefetchInterval(status.scenarioIsRunning ? 3000 : false);

    if (previousStatus?.key === statusKey && previousStatus.isRunning && !status.scenarioIsRunning) {
      queryClient.invalidateQueries({
        queryKey: ['getScenario', companyId, params.scenarioId],
      });

      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: 'Scenario has completed!' },
      });
    }

    previousScenarioRunning.current = { key: statusKey, isRunning: status.scenarioIsRunning };
  }, [
    companyId,
    params.globalSiteId,
    params.scenarioId,
    queryClient,
    scenarioStatusQuery.data,
    snackbarDispatch,
  ]);

  return <ScenarioContext.Provider value={{ scenarioQuery, scenarioStatusQuery }}>{children}</ScenarioContext.Provider>;
};

function useScenarioState() {
  const context = useContext(ScenarioContext);
  if (context === undefined) {
    throw new Error('useScenarioState must be used within a SiteSettingsProvider');
  }
  return context;
}

export { ScenarioProvider, useScenarioState };
